/**
 * @file standingOrderProducts.js
 * @version 1.0.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-10-15
 * @modified 2026-10-15
 * @description A standing order whose product is archived — the one rule
 *              (Cam, 15 Oct 2026: "If a standing order is for an archived
 *              product the user should not be allowed to create an order
 *              from it, instead being prompted to find out what product
 *              should replace the archived one on the standing order").
 *
 *   The item archive is THIS system's (`inventory.items.is_archived`): a
 *   seasonal blend archived here may still be active in Xero, and a standing
 *   order naming it would go on raising orders for a product nobody makes.
 *
 *   - An order is never made from a standing order whose MAIN product is
 *     archived. The person is told to find out from the customer (or their
 *     rep) which product replaces it and change the standing order. Its
 *     active alternatives are named as candidates — never swapped in.
 *   - Lines picked from the customer's POs are refused the same way when
 *     their product is archived.
 *   - Archived alternatives do not travel to the order screen.
 *
 *   Written once here; utils/standingOrderProducts.js on the server is
 *   GENERATED from it by scripts/sync-shared-utils.js (the save's 409 uses
 *   the same words).
 */

/** The refusal's code, on the order save and in the screens alike. */
export const STANDING_ORDER_PRODUCT_ARCHIVED = 'STANDING_ORDER_PRODUCT_ARCHIVED';

/** "10086013 MS Mar 26 Blend" — code first, either alone when that is all there is. */
export function productLabel(product) {
  if (!product) return 'This product';
  const code = String(product.item_code || product.product_code || '').trim();
  const name = String(product.item_name || product.product_name || '').trim();
  if (code && name && code.toLowerCase() !== name.toLowerCase()) return `${code} ${name}`;
  return code || name || 'This product';
}

/** "A", "A and B", "A, B and C". */
export function joinNames(names) {
  const list = (names || []).filter(Boolean);
  if (list.length <= 1) return list[0] || '';
  return `${list.slice(0, -1).join(', ')} and ${list[list.length - 1]}`;
}

/**
 * The standing order's main product: the first of its products, else the
 * single columns it has always carried (item_id / item_code / product_name /
 * product_archived), for a row from before the product list.
 */
export function mainProductOf(order) {
  if (!order) return null;
  const listed = Array.isArray(order.products) ? order.products[0] : null;
  if (listed) return listed;
  if (!order.item_id && !order.item_code && !order.product_code) return null;
  return {
    item_id: order.item_id || null,
    item_code: order.item_code || order.product_code || null,
    item_name: order.product_name || null,
    is_archived: !!(order.main_product_archived ?? order.product_archived)
  };
}

/** Whether the main product is archived here. */
export function mainProductArchived(order) {
  if (!order) return false;
  if (order.main_product_archived !== undefined && order.main_product_archived !== null) {
    return !!order.main_product_archived;
  }
  return !!mainProductOf(order)?.is_archived;
}

/** The alternatives (every product after the main one), split by archived. */
export function alternativesOf(order) {
  const alts = Array.isArray(order?.products) ? order.products.slice(1) : [];
  return {
    active: alts.filter(p => !p.is_archived),
    archived: alts.filter(p => !!p.is_archived)
  };
}

/** Every archived product on the standing order, main first. */
export function archivedProductsOf(order) {
  const listed = Array.isArray(order?.products) ? order.products : [];
  if (listed.length) return listed.filter(p => !!p.is_archived);
  return mainProductArchived(order) ? [mainProductOf(order)] : [];
}

/**
 * The words of the refusal — the screens' dialog and the order save's 409.
 * `products` are the archived ones (label fields as productLabel reads them).
 */
export function archivedRefusalMessage(products) {
  const list = (products || []).filter(Boolean);
  const named = joinNames(list.map(productLabel)) || 'This product';
  const many = list.length > 1;
  return `${named} ${many ? 'are' : 'is'} archived, so an order can't be made from this standing order. `
    + `Find out from the customer (or their rep) which product replaces ${many ? 'them' : 'it'}, `
    + 'then change the standing order\'s product.';
}

/**
 * May an order be made from this standing order now? null when it may;
 * otherwise what to tell the person:
 *   { code, products (the archived ones), candidates (active alternatives —
 *     to choose from in Edit, never swapped in), message, fromLines }
 *
 * `selectedLines` are the customer's PO lines picked in the expanded row,
 * each carrying `item_archived` (from the server) — when any are picked, they
 * are what the order is made from, so they are what is checked.
 */
export function createOrderBlock(order, { selectedLines = [] } = {}) {
  if (!order) return null;
  const candidates = alternativesOf(order).active;
  const picked = (selectedLines || []).filter(Boolean);
  if (picked.length) {
    const archived = picked.filter(l => !!(l.item_archived ?? l.is_archived));
    if (!archived.length) return null;
    // One name per product, however many POs it was picked from.
    const seen = new Set();
    const products = archived.filter((l) => {
      const key = String(l.product_code || l.item_code || l.product_name || '').toLowerCase();
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
    return {
      code: STANDING_ORDER_PRODUCT_ARCHIVED,
      products,
      candidates,
      fromLines: true,
      message: archivedRefusalMessage(products)
    };
  }
  if (!mainProductArchived(order)) return null;
  const main = mainProductOf(order);
  return {
    code: STANDING_ORDER_PRODUCT_ARCHIVED,
    products: [main],
    candidates,
    fromLines: false,
    message: archivedRefusalMessage([main])
  };
}

/**
 * The lines of a hand-off (or an order save) whose product is archived:
 * a line's own `is_archived` flag, or its item among `archivedItemIds`.
 */
export function archivedLinesOf(lines, archivedItemIds = []) {
  const ids = new Set((archivedItemIds || []).map(n => Number(n)).filter(Number.isFinite));
  return (lines || []).filter(l => l && (
    !!l.is_archived
    || (l.item_id != null && ids.has(Number(l.item_id)))
    || (l.inventory_item_id != null && ids.has(Number(l.inventory_item_id)))
  ));
}

/** The note when archived alternatives are left behind on the order screen. */
export function droppedAlternativesNote(archivedAlternatives) {
  const list = (archivedAlternatives || []).filter(Boolean);
  if (!list.length) return '';
  return `${joinNames(list.map(productLabel))} ${list.length > 1 ? 'are' : 'is'} also on this standing order but archived, so ${list.length > 1 ? 'they are' : 'it is'} not offered.`;
}
